import React, { useState, useEffect } from 'react';
import { Database, ChevronDown, ChevronUp } from 'lucide-react';
import { authHeaders } from '../../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatBytes(mb) {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(mb * 1024).toFixed(0)} KB`;
}

const COLLECTION_LABELS = {
  users: 'Users', profiles: 'Profiles', vehicles: 'Vehicles',
  vehicle_approvals: 'Vehicle Approvals', visitors: 'Visitors',
  notices: 'Notices', staff: 'Staff', facilities: 'Facilities',
  gate_passes: 'Gate Passes', invoices: 'Invoices', receipts: 'Receipts',
  notifications: 'Notifications', resident_entries: 'Resident Entries',
  anpr_readings: 'ANPR Readings', push_subscriptions: 'Push Subscriptions',
  guard_records: 'Guard Records',
};

export function CommunityStoragePanel({ communityId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!communityId) return;
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/super-admin/communities/${communityId}/storage`, { headers: authHeaders() });
        if (res.ok) setStats(await res.json());
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('Community storage error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [communityId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gk-teal"></div>
        <span className="text-slate-400 text-xs">Loading storage...</span>
      </div>
    );
  }

  if (!stats) return null;

  const maxBarMb = Math.max(stats.total_storage_mb, 1);

  return (
    <div data-testid="community-storage-panel" className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50">
      <button
        data-testid="toggle-community-storage"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <p className="text-xs font-semibold text-cyan-400 flex items-center gap-1.5">
          <Database className="w-3.5 h-3.5" />
          Storage Usage
        </p>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>

      <div className="flex items-center gap-4 mt-2">
        <div>
          <p className="text-lg font-bold text-white">{formatBytes(stats.total_storage_mb)}</p>
          <p className="text-[10px] text-slate-500">Storage Used</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-300">{stats.total_documents.toLocaleString()}</p>
          <p className="text-[10px] text-slate-500">Documents</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-300">{stats.collections.length}</p>
          <p className="text-[10px] text-slate-500">Active Collections</p>
        </div>
      </div>

      {/* Summary bar */}
      <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
        <div className="h-full bg-gk-teal rounded-full transition-all" style={{ width: '100%' }} />
      </div>

      {expanded && stats.collections.length > 0 && (
        <div data-testid="community-collection-breakdown" className="mt-3 pt-2 border-t border-slate-700/50 space-y-2">
          {stats.collections.map((c) => {
            const pct = maxBarMb > 0 ? Math.min((c.storage_mb / maxBarMb) * 100, 100) : 0;
            return (
              <div key={c.collection} className="flex items-center gap-2">
                <p className="text-[11px] text-slate-300 w-28 truncate flex-shrink-0">{COLLECTION_LABELS[c.collection] || c.collection}</p>
                <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-500/70 rounded-full" style={{ width: `${Math.max(pct, 1)}%` }} />
                </div>
                <p className="text-[10px] text-slate-400 w-16 text-right flex-shrink-0">{c.document_count} docs</p>
                <p className="text-[10px] text-gk-teal w-14 text-right flex-shrink-0 font-medium">{formatBytes(c.storage_mb)}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
